import React, { useState, useMemo } from "react"
import {
  SeriesRow,
  GroupRow,
  SnapshotSummaryData,
  Perspective,
} from "~/api/snapshot_summaries"
import SummaryRow from "./SummaryRow"
import TotalsRow from "./TotalsRow"
import type { Grouping, View } from "./utils"
import Tooltip from "~/components/Tooltip"

interface PerformanceTableProps {
  data: SnapshotSummaryData
  grouping: Grouping
  view: View
  perspective: Perspective
  vintageFrom: number | null
  vintageTo: number | null
  filterAssetClasses: string[]
  filterStages: string[]
  filterSectors: string[]
  filterText?: string
}

const LABEL_HEADER: Record<Grouping, string> = {
  series: "Series",
  by_vintage: "Vintage",
  by_asset_class: "Asset Class",
  by_stage: "Stage",
  by_sector: "Sector",
  by_portfolio_company: "Company",
}

function isSeriesRow(row: SeriesRow | GroupRow): row is SeriesRow {
  return "series" in row
}

function getRows(
  data: SnapshotSummaryData,
  grouping: Grouping,
): (SeriesRow | GroupRow)[] {
  switch (grouping) {
    case "by_vintage":
      return data.by_vintage
    case "by_asset_class":
      return data.by_asset_class
    case "by_stage":
      return data.by_stage
    case "by_sector":
      return data.by_sector
    default:
      return data.series
  }
}

function getColValue(
  row: SeriesRow | GroupRow,
  col: string,
  view: View,
): string | number | null {
  const p = row.performance
  const qa = row.quarter_activity
  const y = row.ytd_performance
  const isSeries = isSeriesRow(row)

  if (col === "label")
    return isSeries ? (row as SeriesRow).series : (row as GroupRow).label
  if (col === "commitment") return row.commitment
  if (col === "series_count")
    return !isSeries ? (row as GroupRow).series_count : null

  if (view === "performance") {
    switch (col) {
      case "vintage":
        return isSeries ? (row as SeriesRow).vintage : null
      case "asset_class_label":
        return isSeries ? (row as SeriesRow).asset_class_label : null
      case "sector":
        return isSeries ? (row as SeriesRow).sector : null
      case "called_pct":
        return p.called_pct
      case "called":
        return p.called
      case "unfunded":
        return p.unfunded
      case "dist":
        return p.dist
      case "dpi":
        return p.dpi
      case "rvpi":
        return p.rvpi
      case "mv":
        return p.mv
      case "tvpi":
        return p.tvpi
      case "irr":
        return isSeries ? p.irr : null
      case "age":
        return isSeries ? p.age : null
      case "pl":
        return p.pl ?? null
      case "mv_pct":
        return isSeries ? p.mv_pct ?? null : null
      case "pl_pct":
        return p.pl_pct ?? null
      case "qoq_pl":
        return qa.pl
      case "qoq_ncf":
        return qa.net_cf
      case "irr_change":
        return qa.irr_change ?? null
      case "qoq_pl_pct":
        return qa.qoq_pl_pct ?? null
    }
  }

  if (view === "quarter_activity") {
    switch (col) {
      case "net_cf":
        return qa.net_cf
      case "pl":
        return qa.pl
      case "delta_mv":
        return qa.delta_mv
      case "delta_mv_pct":
        return qa.delta_mv_pct
      case "called_pct":
        return qa.called_pct
      case "called":
        return qa.called
      case "dist":
        return qa.dist
    }
  }

  if (view === "ytd") {
    switch (col) {
      case "called_pct":
        return y.called_pct
      case "called":
        return y.called
      case "dist":
        return y.dist
      case "delta_mv":
        return y.delta_mv
      case "net_cf":
        return y.net_cf
      case "pl":
        return y.pl
    }
  }

  return null
}

// Safely coerce a value to a finite number, returning 0 for null/undefined/NaN/Infinity.
// Uses Number() to coerce first because Rails serializes BigDecimal columns as JSON strings
// (e.g. "30400000.0"), which Number.isFinite() rejects without coercion.
function safeN(v: unknown): number {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function round(v: number, decimals: number): number {
  const factor = Math.pow(10, decimals)
  return Math.round(v * factor) / factor
}

function computeFilteredTotals(rows: (SeriesRow | GroupRow)[]): GroupRow {
  if (rows.length === 0) {
    return {
      label: "Total",
      series_count: 0,
      commitment: 0,
      performance: {
        called_pct: null,
        called: null,
        unfunded: null,
        dist: null,
        dpi: null,
        rvpi: null,
        mv: null,
        tvpi: null,
        irr: null,
        age: null,
        mv_pct: null,
        pl_pct: null,
        pl: null,
      },
      quarter_activity: {
        net_cf: null,
        pl: null,
        delta_mv: null,
        delta_mv_pct: null,
        called_pct: null,
        called: null,
        dist: null,
      },
      ytd_performance: {
        called_pct: null,
        called: null,
        dist: null,
        delta_mv: null,
        net_cf: null,
        pl: null,
      },
    }
  }

  const totalCommitment = rows.reduce((s, r) => s + safeN(r.commitment), 0)
  const totalMv = rows.reduce((s, r) => s + safeN(r.performance?.mv), 0)
  const totalCalled = rows.reduce((s, r) => s + safeN(r.performance?.called), 0)
  const totalDist = rows.reduce((s, r) => s + safeN(r.performance?.dist), 0)

  const qCalled = rows.reduce(
    (s, r) => s + safeN(r.quarter_activity?.called),
    0,
  )
  const qDist = rows.reduce((s, r) => s + safeN(r.quarter_activity?.dist), 0)
  const qNetCf = rows.reduce((s, r) => s + safeN(r.quarter_activity?.net_cf), 0)
  const qPl = rows.reduce((s, r) => s + safeN(r.quarter_activity?.pl), 0)
  const qDeltaMv = rows.reduce(
    (s, r) => s + safeN(r.quarter_activity?.delta_mv),
    0,
  )
  const priorTotalMv = rows.reduce(
    (s, r) =>
      s + (safeN(r.performance?.mv) - safeN(r.quarter_activity?.delta_mv)),
    0,
  )

  const ytdCalled = rows.reduce(
    (s, r) => s + safeN(r.ytd_performance?.called),
    0,
  )
  const ytdDist = rows.reduce((s, r) => s + safeN(r.ytd_performance?.dist), 0)
  const ytdDeltaMv = rows.reduce(
    (s, r) => s + safeN(r.ytd_performance?.delta_mv),
    0,
  )
  const ytdNetCf = rows.reduce(
    (s, r) => s + safeN(r.ytd_performance?.net_cf),
    0,
  )
  const ytdPl = rows.reduce((s, r) => s + safeN(r.ytd_performance?.pl), 0)

  const tvpi =
    totalCalled > 0 ? round((totalMv + totalDist) / totalCalled, 2) : null
  const dpi = totalCalled > 0 ? round(totalDist / totalCalled, 2) : null
  const rvpi = totalCalled > 0 ? round(totalMv / totalCalled, 2) : null
  const totalPl = totalMv + totalDist - totalCalled
  const seriesCount = rows.reduce(
    (s, r) => s + (!isSeriesRow(r) ? safeN((r as GroupRow).series_count) : 1),
    0,
  )

  return {
    label: "Total",
    series_count: seriesCount,
    commitment: totalCommitment,
    performance: {
      called_pct:
        totalCommitment > 0 ? round(totalCalled / totalCommitment, 4) : null,
      called: totalCalled,
      unfunded: totalCommitment - totalCalled,
      dist: totalDist,
      dpi,
      rvpi,
      mv: totalMv,
      tvpi,
      irr: null,
      age: null,
      mv_pct:
        rows.reduce((s, r) => s + safeN(r.performance?.mv_pct), 0) || null,
      pl: totalPl,
      pl_pct:
        rows.reduce((s, r) => s + safeN(r.performance?.pl_pct), 0) || null,
    },
    quarter_activity: {
      net_cf: qNetCf,
      pl: qPl,
      delta_mv: qDeltaMv,
      delta_mv_pct: priorTotalMv > 0 ? round(qDeltaMv / priorTotalMv, 4) : null,
      called_pct:
        totalCommitment > 0 ? round(qCalled / totalCommitment, 4) : null,
      called: qCalled,
      dist: qDist,
      irr_change: null,
      qoq_pl_pct:
        rows.reduce((s, r) => s + safeN(r.quarter_activity?.qoq_pl_pct), 0) ||
        null,
    },
    ytd_performance: {
      called_pct:
        totalCommitment > 0 ? round(ytdCalled / totalCommitment, 4) : null,
      called: ytdCalled,
      dist: ytdDist,
      delta_mv: ytdDeltaMv,
      net_cf: ytdNetCf,
      pl: ytdPl,
    },
  }
}

const PerformanceTable = ({
  data,
  grouping,
  view,
  perspective,
  vintageFrom,
  vintageTo,
  filterAssetClasses,
  filterStages,
  filterSectors,
  filterText = "",
}: PerformanceTableProps) => {
  const p = perspective === "net" ? "Net" : "Gross"
  const src = "(source: SSC)"

  const { quarter: currentQ } = data.period
  const priorQ = currentQ === 1 ? 4 : currentQ - 1
  const periodStatsLabel = `Time Period Stats (Q${currentQ} vs Q${priorQ})`

  const [sortCol, setSortCol] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc")

  function handleSort(col: string) {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortCol(col)
      setSortDir("desc")
    }
  }

  const rows = getRows(data, grouping)
  const isSeries = grouping === "series"

  const filterTextLower = filterText.toLowerCase()

  const filteredRows = useMemo(() => {
    if (rows.length === 0) return []
    return rows.filter((row) => {
      if (filterTextLower) {
        const label = isSeriesRow(row) ? row.series : row.label
        if (!label.toLowerCase().includes(filterTextLower)) return false
      }
      if (!isSeries || !isSeriesRow(row)) return true
      if (vintageFrom != null && row.vintage < vintageFrom) return false
      if (vintageTo != null && row.vintage > vintageTo) return false
      if (
        filterAssetClasses.length > 0 &&
        !filterAssetClasses.includes(row.asset_class_label)
      )
        return false
      if (filterStages.length > 0 && !filterStages.includes(row.stage ?? ""))
        return false
      if (filterSectors.length > 0 && !filterSectors.includes(row.sector ?? ""))
        return false
      return true
    })
  }, [
    rows,
    isSeries,
    filterTextLower,
    vintageFrom,
    vintageTo,
    filterAssetClasses,
    filterStages,
    filterSectors,
  ])

  const sortedRows = useMemo(() => {
    if (!sortCol) return filteredRows
    return [...filteredRows].sort((a, b) => {
      const av = getColValue(a, sortCol, view)
      const bv = getColValue(b, sortCol, view)
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      const cmp =
        typeof av === "string"
          ? av.localeCompare(bv as string)
          : (av as number) - (bv as number)
      return sortDir === "asc" ? cmp : -cmp
    })
  }, [filteredRows, sortCol, sortDir, view])

  const filteredTotals = useMemo(
    () => computeFilteredTotals(filteredRows),
    [filteredRows],
  )

  const th = (col: string, label: string, tooltip?: string) => (
    <th
      key={col}
      onClick={() => handleSort(col)}
      className={sortCol === col ? "sort-active" : ""}
    >
      <Tooltip tip={tooltip} asChild>
        <span className="b-th-inner">
          <span className="b-th-label">{label}</span>
          <span className="b-sort-indicator">
            {sortCol === col ? (sortDir === "asc" ? "▲" : "▼") : "▼"}
          </span>
        </span>
      </Tooltip>
    </th>
  )

  if (rows.length === 0) {
    return (
      <div className="b-snapshot-summary-no-data">
        <p className="b-snapshot-summary-no-data-message">
          No data available for this grouping.
        </p>
        <p className="b-snapshot-summary-no-data-hint">
          This may be because the series in this period have no associated
          sector data in the uploaded SSC file.
        </p>
      </div>
    )
  }

  return (
    <div className="b-snapshot-summary-table-wrapper">
      <table className="b-snapshot-summary-table">
        <thead>
          {view === "performance" && (
            <tr className="b-snapshot-summary-thead-groups">
              <th colSpan={isSeries ? 14 : 10} className="b-th-identity"></th>
              <th colSpan={3} className="b-th-period-stats">
                {periodStatsLabel}
              </th>
              <th colSpan={3} className="b-th-portfolio-share">
                Portfolio Share
              </th>
            </tr>
          )}
          <tr>
            {th("label", LABEL_HEADER[grouping])}
            {isSeries &&
              th(
                "vintage",
                "Vintage",
                "Year the fund was established / first capital was called",
              )}
            {isSeries &&
              th(
                "asset_class_label",
                "Class",
                "Asset class (e.g. Private Equity, Real Assets)",
              )}
            {isSeries &&
              th("sector", "Sector", "Primary sector focus of the fund")}
            {!isSeries &&
              th(
                "series_count",
                "# Funds",
                "Number of fund series in this group",
              )}
            {th(
              "commitment",
              "Commitment",
              "Total committed capital to this fund",
            )}
            {view === "performance" && (
              <>
                {th(
                  "called_pct",
                  "Called %",
                  `Percentage of total commitment drawn down since inception ${src}`,
                )}
                {th(
                  "called",
                  "Called",
                  `${p} capital called (drawn down) since inception ${src}`,
                )}
                {th(
                  "dist",
                  "Distributions",
                  `Total ${p.toLowerCase()} distributions paid to LPs since inception, after management fees ${src}`,
                )}
                {th(
                  "mv",
                  "Market Value",
                  `${p} NAV: LP-level market value after management fees as of this quarter ${src}`,
                )}
                {th(
                  "pl",
                  "ITD P&L",
                  `Profit & Loss since inception: ${p} MV + ${p} Distributions − ${p} Called`,
                )}
                {th(
                  "tvpi",
                  "TVPI",
                  `${p} TVPI: (${p} MV + ${p} Distributions) ÷ ${p} Called. LP-level total return multiple ${src}`,
                )}
                {isSeries &&
                  th(
                    "irr",
                    "IRR",
                    `${p} IRR: annualized internal rate of return calculated from ${p.toLowerCase()} cash flows ${src}`,
                  )}
                {th(
                  "dpi",
                  "DPI",
                  `${p} DPI: ${p.toLowerCase()} distributions ÷ ${p.toLowerCase()} called capital ${src}`,
                )}
                {isSeries &&
                  th(
                    "age",
                    "Age",
                    "Fund age in years since first capital call",
                  )}
                {th(
                  "qoq_pl",
                  "QoQ P&L",
                  `Quarter-over-quarter profit & loss: change in ${p} MV plus distributions minus calls this quarter ${src}`,
                )}
                {th(
                  "qoq_ncf",
                  "QoQ NCF",
                  `Quarter-over-quarter net cash flow: ${p.toLowerCase()} distributions minus ${p.toLowerCase()} capital calls this quarter ${src}`,
                )}
                {th(
                  "irr_change",
                  "IRR Change",
                  `Change in ${p} IRR versus prior quarter, in percentage points ${src}`,
                )}
                {th(
                  "mv_pct",
                  "% of MV",
                  "This series' market value as a percentage of total portfolio MV",
                )}
                {th(
                  "pl_pct",
                  "% of ITD P&L",
                  "This series' ITD P&L as a percentage of total portfolio ITD P&L",
                )}
                {th(
                  "qoq_pl_pct",
                  "% of QoQ P&L",
                  `This series' quarter-over-quarter P&L as a percentage of total portfolio QoQ P&L ${src}`,
                )}
              </>
            )}
            {view === "quarter_activity" && (
              <>
                {th(
                  "net_cf",
                  "Net CF",
                  `${p} net cash flow this quarter: ${p.toLowerCase()} capital calls minus ${p.toLowerCase()} distributions ${src}`,
                )}
                {th(
                  "pl",
                  "P&L",
                  `Profit & Loss this quarter: change in ${p} MV plus ${p.toLowerCase()} distributions minus ${p.toLowerCase()} calls ${src}`,
                )}
                {th(
                  "delta_mv",
                  "Δ MV",
                  `Change in ${p} market value this quarter ${src}`,
                )}
                {th(
                  "delta_mv_pct",
                  "Δ MV%",
                  `Change in ${p} MV as a percentage of prior quarter-end ${p} MV ${src}`,
                )}
                {th(
                  "called_pct",
                  "Called%",
                  `${p} capital called this quarter as a percentage of total commitment ${src}`,
                )}
                {th(
                  "called",
                  "Called$",
                  `${p} capital called (drawn down) this quarter ${src}`,
                )}
                {th(
                  "dist",
                  "Dist",
                  `${p} distributions paid to LPs this quarter, after management fees ${src}`,
                )}
              </>
            )}
            {view === "ytd" && (
              <>
                {th(
                  "called_pct",
                  "Called%",
                  `${p} capital called year-to-date as a percentage of total commitment ${src}`,
                )}
                {th(
                  "called",
                  "Called$",
                  `${p} capital called year-to-date ${src}`,
                )}
                {th(
                  "dist",
                  "Dist",
                  `${p} distributions paid to LPs year-to-date, after management fees ${src}`,
                )}
                {th(
                  "delta_mv",
                  "Δ MV",
                  `Change in ${p} market value year-to-date ${src}`,
                )}
                {th(
                  "net_cf",
                  "Net CF",
                  `${p} net cash flow year-to-date: ${p.toLowerCase()} capital calls minus ${p.toLowerCase()} distributions ${src}`,
                )}
                {th("pl", "P&L", `${p} Profit & Loss year-to-date ${src}`)}
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {sortedRows.length === 0 ? (
            <tr>
              <td className="b-snapshot-summary-empty" colSpan={99}>
                No results match the active filters.
              </td>
            </tr>
          ) : (
            sortedRows.map((row) => (
              <SummaryRow
                key={
                  "series" in row
                    ? row.net_snapshot_id ?? row.security_id
                    : row.label
                }
                row={row}
                grouping={grouping}
                view={view}
              />
            ))
          )}
        </tbody>
        {sortedRows.length > 0 && (
          <tfoot>
            <TotalsRow
              totals={filteredTotals}
              grouping={grouping}
              view={view}
            />
          </tfoot>
        )}
      </table>
    </div>
  )
}

export default PerformanceTable
